import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Window from "../window/Window";
import Content from "./content/Content";
import {
  selectCurrentSong,
  selectState,
  positionChanged,
  durationChanged,
  selectVolume,
  selectBalance,
  selectPosition,
  freqChanged,
  nextSong,
} from "../appSlice";

function Player() {
  const currentSong = useSelector(selectCurrentSong);
  const state = useSelector(selectState);
  const dispatch = useDispatch();

  const audioRef = useRef<HTMLAudioElement>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const stereoRef = useRef<StereoPannerNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const balance = useSelector(selectBalance);

  useEffect(() => {
    if (stereoRef.current) stereoRef.current.pan.value = balance / 100; // -1 … 1
  }, [balance]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) {
      console.warn(
        "[Player.tsx] Audio element ref not available during initial setup. Analyser will not work if it wasn't already setup."
      );
      return;
    }

    if (!ctxRef.current) {
      try {
        ctxRef.current = new AudioContext();
        console.log("[Player.tsx] AudioContext created successfully.");
      } catch (e) {
        console.error("[Player.tsx] Failed to create AudioContext:", e);
        return; // Cannot proceed without AudioContext
      }
    }

    // This check ensures we only try to create and connect nodes once.
    if (!sourceRef.current) {
      const ctx = ctxRef.current; // Known to be non-null if we reached here
      console.log(
        "[Player.tsx] Attempting to create and connect audio graph nodes."
      );
      try {
        sourceRef.current = ctx.createMediaElementSource(el);
        gainNodeRef.current = ctx.createGain();
        stereoRef.current = ctx.createStereoPanner();
        analyserRef.current = ctx.createAnalyser(); // analyserRef gets set here

        sourceRef.current
          .connect(gainNodeRef.current)
          .connect(stereoRef.current)
          .connect(analyserRef.current)
          .connect(ctx.destination);

        if (el) el.volume = 1;
        if (gainNodeRef.current) gainNodeRef.current.gain.value = volume;

        console.log(
          "[Player.tsx] Audio graph nodes (including GainNode) created and connected successfully. Analyser should be ready."
        );
      } catch (error) {
        console.error(
          "[Player.tsx] Error during audio graph creation/connection:",
          error
        );
        sourceRef.current = null;
        stereoRef.current = null;
        analyserRef.current = null;
        gainNodeRef.current = null;
        console.warn(
          "[Player.tsx] AnalyserNode setup failed; analyserRef is now null."
        );
      }
    } else {
      // console.log("[Player.tsx] Audio graph nodes already exist."); // Optional: log if already set up
    }
  }, []); // Runs once after initial render

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      throw new Error("Not found audio");
    }

    if (state === "played") {
      audio.play();
    }

    return () => {
      // audio.pause();
    };
  }, [state, currentSong, audioRef]);

  // const audioContextRef = useRef<AudioContext>(new AudioContext());
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      throw new Error("Not found audio");
    }

    switch (state) {
      case "paused": {
        audio.pause();
        break;
      }
      case "played": {
        if (audioRef.current) {
          audioRef.current.play();
        }
        // audio.play();
        break;
      }
      case "stopped": {
        audio.pause();
        audio.currentTime = 0;
        break;
      }
    }
  }, [state, audioRef]);

  const volume = useSelector(selectVolume);
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume;
    } else {
      const audio = audioRef.current;
      if (audio) {
        audio.volume = volume;
      }
    }
  }, [volume]);

  useEffect(() => {
    let animationFrameId: number;
    const analyserNode = analyserRef.current;
    const audioCtx = ctxRef.current;

    const cleanup = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        // console.log("[Player.tsx] Bucle del analizador detenido."); // Original log
      }
    };

    if (state === "played" && analyserNode && audioCtx) {
      const startAnimationLoop = () => {
        // console.log("[Player.tsx] Iniciando bucle del analizador de frecuencias. AudioContext state:", audioCtx.state); // Original log
        const animate = () => {
          if (
            analyserRef.current !== analyserNode ||
            ctxRef.current !== audioCtx ||
            state !== "played"
          ) {
            // Stale closure or state changed, stop this loop instance
            cleanup();
            return;
          }
          const freqData = new Uint8Array(analyserNode.frequencyBinCount);
          analyserNode.getByteFrequencyData(freqData);

          const result: number[] = [];
          const bands = 19;
          const actualFrequencyBinCount = analyserNode.frequencyBinCount;
          const elementsPerBand = Math.floor(actualFrequencyBinCount / bands);

          if (elementsPerBand > 0) {
            for (let i = 0; i < bands; ++i) {
              const start = i * elementsPerBand;
              const end = Math.min(
                (i + 1) * elementsPerBand,
                actualFrequencyBinCount
              );
              const analyserPart = freqData.slice(start, end);
              let partSum = 0;
              for (let j = 0; j < analyserPart.length; j++) {
                partSum += analyserPart[j];
              }
              result.push(partSum / (255 * elementsPerBand));
            }
          } else {
            // console.warn("[Player.tsx] No hay suficientes datos de frecuencia para las bandas solicitadas.");
          }
          if (result.length > 0) {
            dispatch(freqChanged(result));
          }

          animationFrameId = requestAnimationFrame(animate);
        };
        animate();
      };

      if (audioCtx.state === "running") {
        startAnimationLoop();
      } else if (audioCtx.state === "suspended") {
        console.log(
          "[Player.tsx] AudioContext está suspendido, intentando reanudar para el analizador."
        );
        audioCtx
          .resume()
          .then(() => {
            // Check again: state could have changed, or refs might be stale if component re-rendered.
            if (
              state === "played" &&
              analyserRef.current === analyserNode &&
              ctxRef.current === audioCtx &&
              audioCtx.state === "running"
            ) {
              // console.log(
              //   "[Player.tsx] AudioContext reanudado, iniciando animación del analizador."
              // );
              startAnimationLoop();
            } else {
              // console.log(
              //   "[Player.tsx] El estado cambió o las refs se invalidaron después de reanudar, no se inicia la animación. Estado actual del contexto:",
              //   audioCtx.state
              // );
            }
          })
          .catch((err) => {
            console.error(
              "[Player.tsx] Error al reanudar AudioContext para el analizador:",
              err
            );
          });
      } else {
        // console.log(
        //   `[Player.tsx] El analizador no se inicia. Estado de AudioContext: ${audioCtx.state}`
        // );
      }
    } else {
      // Existing console logs for why it's not starting
      if (state !== "played") {
        // console.log( // Quieter log, happens often
        //   "[Player.tsx] El analizador no se inicia porque el estado no es 'played'."
        // );
      }
      if (!analyserNode) {
        // console.log(
        //   "[Player.tsx] El analizador no se inicia porque AnalyserNode no está listo."
        // );
      }
      if (!audioCtx && state === "played") {
        // Only log if we expected it to be ready
        // console.log(
        //   "[Player.tsx] El analizador no se inicia porque AudioContext no está listo."
        // );
      }
      if (
        audioCtx &&
        audioCtx.state !== "running" &&
        state === "played" &&
        audioCtx.state !== "suspended"
      ) {
        // Log if not running and not about to be resumed
        // console.log(
        //   "[Player.tsx] El analizador no se inicia porque AudioContext no está 'running' ni 'suspended'. Estado actual:",
        //   audioCtx.state
        // );
      }
    }

    return () => {
      cleanup();
      if (animationFrameId) {
        // ensure it's really cancelled
        cancelAnimationFrame(animationFrameId);
      }
      // console.log("[Player.tsx] Bucle del analizador detenido y limpiado."); // More verbose cleanup log
    };
  }, [state, dispatch]); // analyserRef and ctxRef are from useRef, their identity is stable.

  // const stereoNodeRef = useRef<StereoPannerNode>(
  //   new StereoPannerNode(audioContextRef.current, { pan: 0 })
  // );
  // const analyserNodeRef = useRef<AnalyserNode>(
  //   new AnalyserNode(audioContextRef.current)
  // );

  // useEffect(() => {
  //   console.log(
  //     "[Player.tsx] useEffect for balance triggered. Selected balance:",
  //     balance
  //   );
  //   if (!window.AudioContext) {
  //     console.log("[Player.tsx] AudioContext not available.");
  //     return;
  //   }

  //   if (!stereoNodeRef.current) {
  //     const audio = audioRef.current;
  //     if (!audio) {
  //       console.error(
  //         "[Player.tsx] Audio element not found for stereo panner setup."
  //       );
  //       throw new Error("Not found audio");
  //     }

  //     console.log(
  //       "[Player.tsx] Setting up StereoPannerNode for the first time."
  //     );
  //     const audioContext = audioContextRef.current || new AudioContext(); // Ensure audioContextRef.current is used if already initialized elsewhere or initialize new
  //     if (!audioContextRef.current) audioContextRef.current = audioContext;

  //     const track = audioContext.createMediaElementSource(audio);

  //     stereoNodeRef.current = audioContext.createStereoPanner();
  //     // stereoNodeRef.current = new StereoPannerNode(audioContext, { pan: 0 });
  //     const analyser =
  //       analyserNodeRef.current || new AnalyserNode(audioContext); // Ensure analyserNodeRef.current is used or initialize new
  //     if (!analyserNodeRef.current) analyserNodeRef.current = analyser;

  //     track
  //       .connect(analyser)
  //       .connect(stereoNodeRef.current)
  //       .connect(audioContext.destination);
  //   }

  //   const panValueToSet = balance / 100;
  //   console.log("[Player.tsx] Setting stereoNode.pan.value to:", panValueToSet);
  //   stereoNodeRef.current.pan.value = panValueToSet;
  //   console.log(
  //     "[Player.tsx] stereoNode.pan.value after setting:",
  //     stereoNodeRef.current.pan.value
  //   );

  //   let stopAnalyze = false;

  //   if (state === "played") {
  //     requestAnimationFrame(function animate() {
  //       if (stopAnalyze) {
  //         return;
  //       }

  //       const analyser = analyserNodeRef.current;
  //       if (!analyser) {
  //         throw new Error("Not found audio analyzer");
  //       }

  //       const freqData = new Uint8Array(analyser.frequencyBinCount);
  //       analyser.getByteFrequencyData(freqData);

  //       const result: number[] = [];
  //       for (let i = 0; i < 19; ++i) {
  //         const analyserPart = freqData.slice(i * 25, (i + 1) * 25);
  //         const partSum = analyserPart.reduce((acc, curr) => acc + curr, 0);
  //         result.push(partSum / (256 * 25));
  //       }

  //       dispatch(freqChanged(result));

  //       requestAnimationFrame(animate);
  //     });
  //   }

  //   return () => {
  //     stopAnalyze = true;
  //   };
  // }, [audioRef, balance, state, dispatch]);

  const position = useSelector(selectPosition);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      throw new Error("Not found audio");
    }

    // If position is 0 (indicating a potential restart or start of new song)
    // and audio.currentTime is not already 0, force it to 0.
    // Otherwise, for general seeking, use the threshold to avoid rapid updates
    // fighting with the onTimeUpdate handler.
    if (position === 0 && audio.currentTime !== 0) {
      audio.currentTime = 0;
    } else if (Math.abs(audio.currentTime - position) > 1) {
      audio.currentTime = position;
    }
  }, [audioRef, position]);

  const updatePosition = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    dispatch(positionChanged(Math.floor(event.currentTarget.currentTime)));
  };

  const updateDuration = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    dispatch(durationChanged(event.currentTarget.duration));
  };

  // if (!currentSong) return <></>;

  return (
    <div>
      <Window>
        <Content />
      </Window>

      <audio
        ref={audioRef}
        src={currentSong.path}
        crossOrigin="anonymous"
        onPlay={updatePosition}
        onSeeked={updatePosition}
        onTimeUpdate={updatePosition}
        onLoadedData={updateDuration}
        onEnded={() => dispatch(nextSong(1))}
      />
    </div>
  );
}
export default Player;
